import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import { TbBellFilled } from 'react-icons/tb';
import Modal from '../Modal/Modal';
import { csrfFetch } from '../../utils/csrf';
import type { NotifyMeProps, NotifyMeStep, Packsize } from './types';
import './NotifyMe.scss';

function OtherPacksizes({ packsizes }: { packsizes: Packsize[] }) {
  return (
    <>
      <p>Check product stock in another size:</p>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger className="btn btn-default notify-me-packsizes-trigger">
          Other Packsizes <span className="caret" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="notify-me-packsizes"
            align="start"
            sideOffset={2}
            collisionPadding={16}
          >
            {packsizes.map(({ label, url }) => (
              <DropdownMenu.Item key={url} asChild>
                <a href={url}>{label}</a>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
}

const results = {
  success: {
    title: 'Confirmed!',
    message:
      "We'll send an email to let you know when this product is back in stock.",
  },
  failure: {
    title: 'Something went wrong!',
    message: 'Please try again later.',
  },
};

export default function NotifyMe({
  action,
  email,
  productId,
  packsizes,
  variant,
}: NotifyMeProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<NotifyMeStep>('ask');
  const pending = step === 'pending';
  const result =
    step === 'success' || step === 'failure' ? results[step] : null;
  const notified = step === 'success';

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const body = new FormData(event.currentTarget);
    setStep('pending');
    try {
      const response = await csrfFetch(action, { method: 'POST', body });

      setStep(response.status === 201 ? 'success' : 'failure');
    } catch {
      setStep('failure');
    }
  }

  function openModal() {
    setStep('ask');
    setOpen(true);
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={
          variant === 'card'
            ? 'notify-me-trigger notify-me-trigger--card'
            : clsx('notify-me-trigger btn btn-primary', notified && 'disabled')
        }
        data-notify-me-product-id={productId}
        aria-disabled={notified || undefined}
        onClick={notified ? undefined : openModal}
      >
        {notified ? (
          <span>Stock arriving soon</span>
        ) : (
          <>
            <TbBellFilled aria-hidden="true" />{' '}
            <span>Notify me when back in stock</span>
          </>
        )}
      </button>
      <Modal
        open={open}
        onOpenChange={(next) => !pending && setOpen(next)}
        returnFocusRef={triggerRef}
        className="notify-me-modal"
      >
        <div className="bio-modal__header">
          <Dialog.Close asChild>
            <button
              type="button"
              className="bio-modal__close"
              aria-label="Close"
              disabled={pending}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </Dialog.Close>
          <Dialog.Title>
            {result?.title ?? 'Notify when back in stock?'}
          </Dialog.Title>
        </div>
        {!result ? (
          <>
            <div className="bio-modal__body">
              {packsizes.length > 0 && <OtherPacksizes packsizes={packsizes} />}
              <p>
                Would you like to receive an email when this product is back in
                stock?
              </p>
              <p>
                <b>{email}</b>
              </p>
            </div>
            <form
              className="bio-modal__footer"
              action={action}
              method="post"
              onSubmit={submit}
            >
              <input type="hidden" name="product_id" value={productId} />
              <button
                type="submit"
                className="btn btn-danger pr-20 pl-20"
                disabled={pending}
              >
                Yes, notify me
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="bio-modal__body">
              <p>{result.message}</p>
            </div>
            <div className="bio-modal__footer">
              <Dialog.Close className="btn btn-danger pr-20 pl-20">
                <b>Close</b>
              </Dialog.Close>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
